<template>
  <q-page class="set-password-page">
    <div class="set-password-content">
      <!-- Expired or already consumed link -->
      <div v-if="linkExpired" class="header-section">
        <h1 class="title">{{ $t('login.errorMagicLinkExpired') }}</h1>
        <q-btn
          no-caps
          unelevated
          class="submit-button"
          data-cy="magic-link-expired-retry"
          :label="$t('login.backToFirstLogin')"
          @click="goToFirstLogin"
        />
      </div>

      <template v-else-if="!passwordUpdated">
        <div class="header-section">
          <div class="icon-badge">
            <q-icon name="lock_outline" size="26px" />
          </div>
          <h1 class="title">{{ $t('login.setPasswordTitle') }}</h1>
          <p class="subtitle">{{ $t('login.setPasswordDescription') }}</p>
        </div>

        <form class="set-password-form" @submit.prevent="onSubmit">
          <div class="field">
            <label class="field-label" for="magic-password">
              {{ $t('login.password') }}
            </label>
            <q-input
              id="magic-password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('login.passwordPlaceholder')"
              autocomplete="new-password"
              outlined
              hide-bottom-space
              data-cy="magic-password"
              class="field-input"
            >
              <template #append>
                <q-icon
                  :name="showPassword ? 'visibility_off' : 'visibility'"
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                />
              </template>
            </q-input>
          </div>

          <div class="field">
            <label class="field-label" for="magic-password-confirmation">
              {{ $t('login.confirmPassword') }}
            </label>
            <q-input
              id="magic-password-confirmation"
              v-model="confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              :placeholder="$t('login.confirmPasswordPlaceholder')"
              :error="!!confirmPasswordError"
              :error-message="confirmPasswordError"
              autocomplete="new-password"
              outlined
              data-cy="magic-password-confirmation"
              class="field-input"
            >
              <template #append>
                <q-icon
                  :name="showConfirmPassword ? 'visibility_off' : 'visibility'"
                  class="password-toggle"
                  @click="showConfirmPassword = !showConfirmPassword"
                />
              </template>
            </q-input>
          </div>

          <div class="requirements" data-cy="password-requirements">
            <div
              v-for="requirement in passwordRequirements"
              :key="requirement.key"
              class="requirement"
              :class="{ 'requirement--valid': requirement.valid }"
            >
              <q-icon
                :name="
                  requirement.valid ? 'check_circle' : 'radio_button_unchecked'
                "
                size="16px"
                class="requirement-icon"
              />
              <span class="requirement-label">{{ requirement.label }}</span>
            </div>
          </div>

          <div
            v-if="serverError"
            class="server-error"
            data-cy="set-password-error"
          >
            <q-icon
              name="error_outline"
              size="18px"
              class="server-error-icon"
            />
            <span>{{ serverError }}</span>
          </div>

          <q-btn
            type="submit"
            icon="lock"
            no-caps
            unelevated
            class="submit-button"
            data-cy="save-new-password"
            :label="$t('login.saveNewPassword')"
            :loading="loading"
            :disable="!canSubmit || loading"
          />
        </form>
      </template>

      <div v-else class="header-section" data-cy="set-password-success">
        <div class="success-icon-outer">
          <div class="success-icon-inner">
            <q-icon name="check" size="24px" color="white" />
          </div>
        </div>
        <h1 class="title">{{ $t('login.passwordSaved') }}</h1>
        <p class="subtitle">
          {{ $t('login.magicLinkPasswordChangedSubtitle') }}
        </p>
        <q-btn
          no-caps
          unelevated
          class="submit-button"
          data-cy="go-to-login"
          :label="$t('login.connectMe')"
          @click="goToLoginPage"
        />
      </div>
    </div>
  </q-page>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useValidateMagicLink } from 'src/composables/useValidateMagicLink'

export default {
  name: 'SetPassword',
  setup() {
    const route = useRoute()
    const router = useRouter()

    // Untrusted input: only ever sent as a POST body field, never interpolated
    // into a path, rendered as HTML or logged.
    const magicToken = computed(() => (route.query.magic || '').trim())

    onMounted(() => {
      if (!magicToken.value) {
        router.replace({ path: '/login' })
      }
    })

    return { ...useValidateMagicLink(magicToken) }
  }
}
</script>

<style lang="scss" scoped>
.set-password-page {
  background: white;
  display: flex;
  flex-direction: column;
}

.set-password-content {
  flex: 1;
  padding: 24px 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.header-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.icon-badge {
  width: 66px;
  height: 66px;
  border-radius: 9999px;
  background: rgba(64, 196, 255, 0.15);
  color: #40c4ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3em;
  color: #152f3b;
  margin: 0;
}

.subtitle {
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4em;
  color: #3e484f;
  margin: 0;
}

.set-password-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 15px;
  font-weight: 600;
  color: #152f3b;
}

.field-input :deep(.q-field__control) {
  height: 52px;
  border-radius: 10px;
}

.field-input :deep(.q-field__marginal) {
  height: 52px;
}

.password-toggle {
  cursor: pointer;
  color: #103140;
  font-size: 22px;
}

.requirements {
  background: #f2f4f6;
  border-radius: 8px;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3e484f;
}

.requirement-icon {
  color: #bbcbd2;
  flex-shrink: 0;
}

.requirement--valid,
.requirement--valid .requirement-icon {
  color: #06d6a0;
}

.requirement-label {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2em;
}

.server-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(239, 71, 111, 0.1);
  border-radius: 8px;
  padding: 12px;
  color: #ef476f;
  font-size: 13px;
  line-height: 1.35em;
}

.server-error-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.submit-button {
  width: 100%;
  height: 54px;
  background: #40c4ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border-radius: 15px;
}

.submit-button :deep(.q-icon) {
  font-size: 18px;
}

.success-icon-outer {
  width: 96px;
  height: 96px;
  border-radius: 9999px;
  background: rgba(6, 214, 160, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon-inner {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: #06d6a0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
